import { getSourceTypeLabel } from './ragSourceUtils';

export const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();

const PHP_HTML_ENTITY_REPLACEMENTS = {
    amp: '&',
    quot: '"',
    apos: "'",
    lt: '<',
    gt: '>',
};

const PHP_HTML_CODE_POINT_REPLACEMENTS = {
    34: '"',
    38: '&',
    39: "'",
    60: '<',
    62: '>',
};

/**
 * Decode the small entity set emitted by PHP's htmlspecialchars at the final
 * React text-node boundary. Calling this after inline markup has been parsed
 * prevents encoded text from turning into new links, citations, or markup.
 */
export const decodeRagTerminalText = (value) => {
    let decoded = String(value ?? '');

    for (let pass = 0; pass < 3; pass += 1) {
        const normalized = decoded.replace(/&\\+#(?=(?:x[0-9a-f]+|\d+);)/gi, '&#');
        const next = normalized.replace(
            /&(amp|quot|apos|lt|gt|#(?:x[0-9a-f]+|\d+));/gi,
            (entity, key) => {
                const normalizedKey = String(key).toLowerCase();
                if (!normalizedKey.startsWith('#')) {
                    return PHP_HTML_ENTITY_REPLACEMENTS[normalizedKey] ?? entity;
                }
                const codePoint = normalizedKey.startsWith('#x')
                    ? Number.parseInt(normalizedKey.slice(2), 16)
                    : Number.parseInt(normalizedKey.slice(1), 10);
                return PHP_HTML_CODE_POINT_REPLACEMENTS[codePoint] ?? entity;
            }
        );
        decoded = next;
        if (next === normalized) break;
    }

    return decoded;
};

export const getSafeRagInlineRecordUrl = (value) => {
    const url = String(value || '');
    return /^\/(?:case|contact)\/[1-9]\d*$/.test(url) ? url : '';
};

const RAG_ALLOWED_RELATIVE_NAVIGATION_ROUTES = [
    /^\/case\/[1-9]\d*\/?(?:[?#].*)?$/i,
    /^\/file\/[1-9]\d*\/?(?:[?#].*)?$/i,
    /^\/lead\/[1-9]\d*\/?(?:[?#].*)?$/i,
    /^\/summaries\/[1-9]\d*\/?(?:[?#].*)?$/i,
    /^\/reports\/?(?:[?#].*)?$/i,
];

/**
 * Return a browser-safe Jane link or an empty string.
 *
 * Source and artifact URLs can originate in retrieved metadata, generated
 * answers, or older saved chats. Keep the navigation policy in one place so
 * every href/window.open caller applies the same protocol boundary. External
 * HTTP(S) links are supported; relative links are limited to known CaseDB
 * read-only views. Generated mailto links are intentionally handled outside
 * this helper.
 */
export const getSafeRagNavigationUrl = (value) => {
    const rawUrl = String(value || '').trim();
    if (!rawUrl || /[\u0000-\u001f\u007f\\]/u.test(rawUrl)) return '';

    if (rawUrl.startsWith('/') && !rawUrl.startsWith('//')) {
        return RAG_ALLOWED_RELATIVE_NAVIGATION_ROUTES.some((pattern) => pattern.test(rawUrl))
            ? rawUrl
            : '';
    }

    try {
        const parsed = new URL(rawUrl);
        if (!['http:', 'https:'].includes(parsed.protocol.toLowerCase())) return '';
        if (!parsed.hostname || parsed.username || parsed.password) return '';
        return rawUrl;
    } catch (error) {
        return '';
    }
};

export const getCaseNameForDisplay = (source = {}, fallback = '') => {
    const value = source && typeof source === 'object' ? source : {};
    return value.case_name
        || value.caseName
        || value.case_title
        || value.caseTitle
        || fallback
        || '';
};

export const replaceCaseNameToken = (value, source = {}, fallback = '') => {
    const text = String(value || '');
    if (!text.includes('{{Name}}')) return text;
    return text.replace(/{{Name}}/g, getCaseNameForDisplay(source, fallback) || 'Case');
};

export const uniqueDisplayParts = (parts = []) => {
    const seen = new Set();
    return parts
        .map((part) => String(part || '').trim())
        .filter((part) => {
            if (!part) return false;
            const key = part.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

export const isGenericCaseLabel = (value, caseId) => {
    const label = String(value || '').trim();
    if (!label || !caseId) return !label;
    return new RegExp(`^case\\s*(id\\s*)?${String(caseId).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'i').test(label);
};

export const getCaseSourceTitle = (citation) => {
    const caseName = String(citation?.case_name || '').trim();
    if (caseName) return caseName;

    const fileName = String(citation?.file_name || '').trim();
    if (fileName && !isGenericCaseLabel(fileName, citation?.case_id)) return fileName;

    return 'Case';
};

export const isCaseSummaryCitation = (citation) => (
    citation?.source_type === 'case'
    || citation?.source_type === 'case_profile'
    || citation?.chunk_source === 'case_summary'
);

export const getRagCitationHref = (citation) => {
    if (!citation) return '';
    if (isCaseSummaryCitation(citation) && citation.case_id) return getSafeRagNavigationUrl(`https://casedb.co/case/${citation.case_id}`);
    if (citation.file_id) return getSafeRagNavigationUrl(`https://casedb.co/file/${citation.file_id}`);
    if (citation.id && /(?:file|document|attachment)/i.test(String(citation.source_type || citation.type || citation.chunk_source || ''))) {
        return getSafeRagNavigationUrl(`https://casedb.co/file/${citation.id}`);
    }
    if (citation.url) return getSafeRagNavigationUrl(citation.url);
    const sourceType = String(citation.source_type || citation.type || citation.chunk_source || '').toLowerCase();
    if (citation.case_id && /case|feed|activity|field|fusion|timeline/.test(sourceType)) return getSafeRagNavigationUrl(`https://casedb.co/case/${citation.case_id}`);
    return '';
};

export const extractPromptLinkedResources = (content = '') => {
    const value = String(content || '');
    const resources = new Map();
    const addUrlResource = (url = '', label = '') => {
        const normalizedUrl = String(url || '').trim();
        if (!/^https?:\/\//i.test(normalizedUrl)) return;
        const decodedPath = (() => {
            try {
                return decodeURIComponent(new URL(normalizedUrl).pathname.split('/').pop() || '');
            } catch (error) {
                return '';
            }
        })();
        const fileName = String(label || decodedPath || normalizedUrl).trim();
        const key = `url-${normalizedUrl}`;
        if (resources.has(key)) return;
        resources.set(key, {
            id: `prompt-${key}`,
            type: /casedb\.s3\.amazonaws\.com/i.test(normalizedUrl) ? 's3_document' : 'url',
            label: fileName,
            file_name: fileName,
            url: normalizedUrl,
            source_type: /casedb\.s3\.amazonaws\.com/i.test(normalizedUrl) ? 's3_document' : 'url',
        });
    };
    const addResource = (type, id, url = '') => {
        const normalizedId = Number(id);
        if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;
        const normalizedType = String(type || '').toLowerCase();
        const labels = {
            case: 'Case',
            file: 'Document',
            lead: 'Lead',
            document: 'Document',
            report: 'Report',
        };
        const key = `${normalizedType}-${normalizedId}`;
        if (resources.has(key)) return;
        resources.set(key, {
            id: `prompt-${key}`,
            type: normalizedType,
            label: `${labels[normalizedType] || 'Linked item'} #${normalizedId}`,
            url: url || (normalizedType === 'report' ? `https://casedb.co/reports?id=${normalizedId}` : `https://casedb.co/${normalizedType === 'document' ? 'file' : normalizedType}/${normalizedId}`),
        });
    };

    const urlPattern = /https?:\/\/(?:www\.)?casedb\.co\/(case|file|lead|document)\/(\d+)/gi;
    let urlMatch;
    while ((urlMatch = urlPattern.exec(value)) !== null) {
        addResource(urlMatch[1] === 'document' ? 'file' : urlMatch[1], urlMatch[2], urlMatch[0]);
    }

    const reportUrlPattern = /https?:\/\/(?:www\.)?casedb\.co\/reports\?(?:[^\s#]*&)?id=(\d+)/gi;
    let reportUrlMatch;
    while ((reportUrlMatch = reportUrlPattern.exec(value)) !== null) {
        addResource('report', reportUrlMatch[1], reportUrlMatch[0]);
    }

    const markdownUrlPattern = /\[([^\]]{1,240})\]\((https?:\/\/[^)\s]+)\)/gi;
    let markdownUrlMatch;
    while ((markdownUrlMatch = markdownUrlPattern.exec(value)) !== null) {
        addUrlResource(markdownUrlMatch[2], markdownUrlMatch[1]);
    }

    const s3UrlPattern = /https?:\/\/casedb\.s3\.amazonaws\.com\/[^\s)]+/gi;
    let s3UrlMatch;
    while ((s3UrlMatch = s3UrlPattern.exec(value)) !== null) {
        addUrlResource(s3UrlMatch[0]);
    }

    const shorthandPattern = /\b(case|file|document|lead|report)\s*(?:id\s*)?#?\s*(\d{3,})\b/gi;
    let shorthandMatch;
    while ((shorthandMatch = shorthandPattern.exec(value)) !== null) {
        addResource(shorthandMatch[1] === 'document' ? 'file' : shorthandMatch[1], shorthandMatch[2]);
    }

    return Array.from(resources.values());
};

export const buildRagRequestReferenceContext = (references = []) => (
    Array.isArray(references) ? references : []
).map((reference) => {
    const type = String(reference?.type || reference?.source_type || '').toLowerCase();
    const fileId = Number(reference?.file_id || ((type.includes('file') || type.includes('document')) ? reference?.id : 0) || 0);
    const caseId = Number(reference?.case_id || (type === 'case' ? reference?.id : 0) || 0);
    const leadId = Number(reference?.lead_id || (type === 'lead' ? reference?.id : 0) || 0);
    return {
        type: type || 'reference',
        source_type: String(reference?.source_type || type || 'reference'),
        label: String(reference?.label || reference?.file_name || reference?.name || reference?.title || '').slice(0, 240),
        file_name: String(reference?.file_name || reference?.name || reference?.label || '').slice(0, 240),
        url: String(reference?.url || '').slice(0, 1200),
        file_id: Number.isFinite(fileId) && fileId > 0 ? fileId : null,
        case_id: Number.isFinite(caseId) && caseId > 0 ? caseId : null,
        lead_id: Number.isFinite(leadId) && leadId > 0 ? leadId : null,
        file_path: String(reference?.file_path || reference?.path || reference?.key || reference?.storage_key || '').slice(0, 1200),
    };
}).filter(reference => reference.file_id || reference.case_id || reference.lead_id || reference.url || reference.file_path);

export const normalizeAssistantActiveCase = (message = {}) => {
    const answer = message.answer || {};
    const metadata = message.metadata || {};
    const intelligence = metadata.intelligence || {};
    const plan = intelligence.plan || {};
    const candidates = [
        answer.active_case,
        intelligence.active_case,
        plan.active_case,
        metadata.active_case,
        message.active_case,
    ].filter(Boolean);
    const candidate = candidates.find((item) => item && typeof item === 'object') || {};
    const caseId = Number(
        candidate.case_id
        || candidate.id
        || answer.active_case_id
        || intelligence.active_case_id
        || plan.active_case_id
        || metadata.active_case_id
        || message.active_case_id
    );
    if (!Number.isFinite(caseId) || caseId <= 0) return null;
    const caseName = String(
        candidate.case_name
        || candidate.name
        || answer.case_name
        || intelligence.case_name
        || plan.case_name
        || ''
    ).trim();
    return {
        case_id: caseId,
        case_name: caseName,
    };
};

export const getCaseReferenceFromAssistantMessage = (message = {}) => {
    if (!message || message.role !== 'assistant') return [];
    const activeCase = normalizeAssistantActiveCase(message);
    if (activeCase) {
        return [{
            id: `prompt-case-${activeCase.case_id}`,
            type: 'case',
            label: activeCase.case_name ? `Case #${activeCase.case_id} · ${activeCase.case_name}` : `Case #${activeCase.case_id}`,
            url: `https://casedb.co/case/${activeCase.case_id}`,
            case_id: activeCase.case_id,
        }];
    }
    const answer = message.answer || {};
    const citations = Array.isArray(answer.citations) ? answer.citations : Array.isArray(message.sources) ? message.sources : [];
    const caseCitation = citations.find(citation => isCaseSummaryCitation(citation) && citation?.case_id)
        || (Array.isArray(message.files) ? message.files.find(file => file?.type === 'case' && file?.case_id) : null);
    const caseId = Number(caseCitation?.case_id || caseCitation?.id);
    if (!Number.isFinite(caseId) || caseId <= 0) return [];
    const caseName = caseCitation?.case_name || caseCitation?.file_name || caseCitation?.name || '';
    return [{
        id: `prompt-case-${caseId}`,
        type: 'case',
        label: caseName ? `Case #${caseId} · ${caseName}` : `Case #${caseId}`,
        url: `https://casedb.co/case/${caseId}`,
        case_id: caseId,
    }];
};

export const getMessagePromptReferences = (message = {}, nextMessage = null) => {
    const explicitReferences = Array.isArray(message.references) ? message.references : [];
    const typedReferences = extractPromptLinkedResources(message.content || message.query_text || '');
    const merged = new Map();
    [...explicitReferences, ...typedReferences].forEach((reference) => {
        const key = reference.url || `${reference.type || 'reference'}-${reference.file_id || reference.case_id || reference.lead_id || reference.id || reference.label}`;
        if (!merged.has(key)) merged.set(key, reference);
    });
    return Array.from(merged.values());
};

export const getRagReferenceDisplayLabel = (reference = {}) => {
    const type = String(reference?.type || reference?.source_type || '').toLowerCase();
    const isChatUpload = type === 'chat_upload' || type === 'upload';
    const label = isChatUpload
        ? (reference.versioned_file_name || reference.file_name || reference.name || reference.label)
        : (reference.label || reference.file_name || reference.name);
    return String(label || 'Reference').trim() || 'Reference';
};

export const getSourceEntryKey = (source = {}) => {
    const type = String(source.type || source.source_type || '').toLowerCase();
    const fileId = source.file_id || (type.includes('file') || type.includes('document') ? source.id : null);
    const caseId = source.case_id || (type.includes('case') ? source.id : null);
    const leadId = source.lead_id || (type.includes('lead') ? source.id : null);
    if (fileId) return `file-${fileId}`;
    if (caseId) return `case-${caseId}`;
    if (leadId) return `lead-${leadId}`;
    return `source-${source.url || source.label || source.name || source.file_name || JSON.stringify(source).slice(0, 80)}`;
};

export const getSourceFileId = (source = {}) => {
    const type = String(source.type || source.source_type || source.chunk_source || '').toLowerCase();
    const fileId = source.file_id || source.fileId || ((type.includes('file') || type.includes('document') || type.includes('attachment')) ? source.id : null);
    const normalized = Number(fileId);
    return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

export const getSourceEntryUrl = (source = {}) => {
    const type = String(source.type || source.source_type || '').toLowerCase();
    const fileId = source.file_id || ((type.includes('file') || type.includes('document')) ? source.id : null);
    const caseId = source.case_id || (type.includes('case') ? source.id : null);
    const leadId = source.lead_id || (type.includes('lead') ? source.id : null);
    if (fileId) return getSafeRagNavigationUrl(`https://casedb.co/file/${fileId}`);
    if (caseId) return getSafeRagNavigationUrl(`https://casedb.co/case/${caseId}`);
    if (leadId) return getSafeRagNavigationUrl(`https://casedb.co/lead/${leadId}`);
    if (source.url) return getSafeRagNavigationUrl(source.url);
    return '';
};

export const getSourceEntryTitle = (source = {}) => (
    source.chunk_source === 'feed_attachment' && source.excerpt
        ? String(source.excerpt).replace(/^Attached to feed update #\d+:\s*/i, '').trim()
            ? `${String(source.excerpt).replace(/^Attached to feed update #\d+:\s*/i, '').trim()} - attachment`
            : ''
        : ''
) || (
    source.label
    || source.source_label
    || source.case_name
    || source.lead_name
    || source.file_name
    || source.name
    || source.title
    || `${getSourceTypeLabel(source.type || source.source_type)} ${source.file_id || source.case_id || source.lead_id || source.id || ''}`.trim()
);

export const getSourceCaseDisplay = (source = {}) => {
    const type = String(source.type || source.source_type || '').toLowerCase();
    const caseId = Number(source.case_id || (type.includes('case') ? source.id : null));
    if (!Number.isFinite(caseId) || caseId <= 0) return null;

    const explicitName = String(source.case_name || source.name || source.title || '').trim();
    const label = String(source.label || source.source_label || '').trim();
    const labelMatch = label.match(/^Case\s*#?\s*\d+\s*[·.-]\s*(.+)$/i);
    const caseName = explicitName && !isGenericCaseLabel(explicitName, caseId)
        ? explicitName
        : (labelMatch?.[1]?.trim() || 'Case');

    return { caseId, caseName };
};

export const getFeedAttachmentSubject = (source = {}) => {
    if (source.chunk_source !== 'feed_attachment' || !source.excerpt) return '';
    return String(source.excerpt).replace(/^Attached to feed update #\d+:\s*/i, '').trim();
};

export const buildRagSourceHistory = (messages = []) => {
    const entries = [];
    const seen = new Set();
    const addSource = (source, message, messageIndex, origin, previewCitation = null) => {
        if (!source || typeof source !== 'object') return;
        const key = getSourceEntryKey(source);
        if (seen.has(key)) return;
        const title = getSourceEntryTitle(source);
        if (!title) return;
        seen.add(key);
        const caseDisplay = getSourceCaseDisplay(source);
        const sourceCaseName = getCaseNameForDisplay(source, caseDisplay?.caseName || '');
        entries.push({
            key,
            title,
            feedAttachmentSubject: getFeedAttachmentSubject(source),
            caseDisplay,
            source,
            fileId: getSourceFileId(source),
            type: getSourceTypeLabel(source.type || source.source_type || source.chunk_source),
            url: getSourceEntryUrl(source) || getRagCitationHref(source),
            date: message.created_at || '',
            origin,
            messageIndex,
            detail: uniqueDisplayParts([
                sourceCaseName,
                source.file_name,
                source.section_name,
                replaceCaseNameToken(source.folder_path, source, sourceCaseName),
            ]).join(' · '),
            previewCitation,
        });
    };

    messages.forEach((message, messageIndex) => {
        if (message.role === 'user') {
            getMessagePromptReferences(message, messages[messageIndex + 1]).forEach(reference => addSource(reference, message, messageIndex, 'Linked in prompt'));
            return;
        }

        const answer = message.answer || {};
        const citations = Array.isArray(answer.citations) ? answer.citations : Array.isArray(message.sources) ? message.sources : [];
        citations.forEach(citation => addSource(citation, message, messageIndex, 'Cited in answer', citation));
        (Array.isArray(message.files) ? message.files : []).forEach(file => addSource(file, message, messageIndex, 'Retrieved for answer'));

        const followupSource = message.metadata?.intelligence?.plan?.followup_source;
        if (followupSource && typeof followupSource === 'object') {
            addSource(followupSource, message, messageIndex, 'Follow-up context');
        }
    });

    return entries;
};

export const getPlainChunkText = (matchedChunk, contextChunk) => stripHtml(
    contextChunk?.chunk_text
    || matchedChunk?.text
    || matchedChunk?.grouped_text
    || matchedChunk?.snippet
    || ''
);

export const getFileExtension = (item) => {
    const source = String(item?.extension || item?.file_name || item?.versioned_file_name || item?.label || item?.name || item?.key || item?.url || '').toLowerCase();
    const match = source.match(/\.([a-z0-9]+)(?:$|[?#])/i);
    return match ? match[1] : '';
};

export const buildCaseDocumentLocationUrl = (source = {}) => {
    const caseId = source.caseId || source.case_id;
    const fileName = source.fileName || source.file_name || source.name || '';
    const fileId = source.fileId || source.file_id;
    if (!caseId) return fileId ? getSafeRagNavigationUrl(`https://casedb.co/file/${fileId}`) : '';

    const params = new URLSearchParams({ section: '3' });
    const rawKey = String(source.storageKey || source.storage_key || source.key || source.file_path || source.path || '');
    if (!rawKey && fileId) return getSafeRagNavigationUrl(`https://casedb.co/file/${fileId}`);
    const keyParts = rawKey.split('/').filter(Boolean);
    let folderParts = [];
    let resolvedFileName = fileName;
    const nameIndex = keyParts.findIndex((part) => part === '{{Name}}');

    if (nameIndex !== -1) {
        folderParts = keyParts.slice(nameIndex + 1, -1);
        resolvedFileName = resolvedFileName || keyParts[keyParts.length - 1] || '';
    } else if (rawKey && !rawKey.startsWith('https://')) {
        const casesIndex = keyParts.findIndex((part) => part === 'cases');
        const afterCaseIndex = casesIndex !== -1 ? casesIndex + 2 : -1;
        if (afterCaseIndex > 0 && keyParts.length > afterCaseIndex) {
            folderParts = keyParts.slice(afterCaseIndex, -1).filter((part) => part !== String(caseId));
            resolvedFileName = resolvedFileName || keyParts[keyParts.length - 1] || '';
        }
    }

    if (folderParts.length > 0) params.set('folder', folderParts.join('/'));
    if (resolvedFileName) params.set('file', resolvedFileName);
    return getSafeRagNavigationUrl(`/case/${caseId}?${params.toString()}`);
};

export const getCaseDocumentFolderLabel = (source = {}) => {
    const rawKey = String(source.storageKey || source.storage_key || source.key || source.file_path || source.path || '');
    if (!rawKey || rawKey.startsWith('https://')) return '';

    const keyParts = rawKey.split('/').filter(Boolean);
    const nameIndex = keyParts.findIndex((part) => part === '{{Name}}');
    if (nameIndex !== -1) {
        const caseName = getCaseNameForDisplay(source);
        return [caseName, ...keyParts.slice(nameIndex + 1, -1)].filter(Boolean).join(' / ');
    }

    const casesIndex = keyParts.findIndex((part) => part === 'cases');
    if (casesIndex !== -1 && keyParts.length > casesIndex + 3) {
        return keyParts.slice(casesIndex + 2, -1)
            .map((part) => part === '{{Name}}' ? (getCaseNameForDisplay(source) || 'Case') : part)
            .join(' / ');
    }

    return '';
};

export const buildPreviewDetailParts = (preview = {}) => [
    preview.caseName || (preview.caseId ? `Case ${preview.caseId}` : ''),
    getCaseDocumentFolderLabel(preview),
    preview.pageLabel,
    preview.chunkIndex !== null && preview.chunkIndex !== undefined && preview.chunkIndex !== ''
        && Number.isFinite(Number(preview.chunkIndex))
        ? `Chunk ${Number(preview.chunkIndex) + 1}`
        : '',
].filter(Boolean);
